import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { Map } from 'immutable';
import { FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { IStory } from '@opengov/stories-shared/types';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';

import { IStoriesFolder, IStoryListMessages } from '../../../../types';

import { navigateToFolderList, navigateToPublicationFolderList } from '../../../state/navigation';
import { transformFolderStructureToMap } from '../../../utils';
import { useMoveStories } from '../hooks';

import { styles } from './MoveStoryModal.styles';

export interface IMoveStoryModalProps extends RouteComponentProps {
  messages: IStoryListMessages;
  entitySubdomain: string;
  selectedStories: Map<string, IStory>;
  folderStructure: IStoriesFolder[];
  currentFolderId?: string;
  publicationId?: string;
  handleMoveStoriesSuccess(): void;
  onClose(): void;
}

const displayStoryList = (selectedStories: Map<string, IStory>) => (
  <Box component="div">
    <Box component="ul" data-test="selected-stories-list">
      {selectedStories.toList().map(story => (
        <li key={story._id}>{story.title}</li>
      ))}
    </Box>
  </Box>
);

const renderModalDescription = (
  selectedStories: Map<string, IStory>,
  folderStructure: IStoriesFolder[],
  messages: IStoryListMessages
) => {
  const folderMap = transformFolderStructureToMap(folderStructure);
  if (selectedStories.size > 1) {
    let currentParentFolders = Map<string, IStoriesFolder>([]);
    selectedStories.forEach(story => {
      if (story.folderId && folderMap[story.folderId]) {
        currentParentFolders = currentParentFolders.set(story.folderId, folderMap[story.folderId]);
      }
    });
    if (currentParentFolders.size > 1) {
      let folderListString = '';
      currentParentFolders.toList().forEach((folder, index) => {
        if (index === currentParentFolders.size - 1) {
          folderListString += `${folder.displayName}`;
        } else if (index === currentParentFolders.size - 2) {
          folderListString += `${folder.displayName} and `;
        } else {
          folderListString += `${folder.displayName}, `;
        }
      });
      return (
        <Typography variant="body2">
          {messages.movingStoriesFrom}
          <Tooltip title={folderListString}>
            <Typography component="span" variant="body2" fontWeight="bold">
              {` ${messages.multipleFolders}:`}
            </Typography>
          </Tooltip>
          {displayStoryList(selectedStories)}
        </Typography>
      );
    }
    if (currentParentFolders.first()) {
      return (
        <Typography variant="body2">
          {`${messages.movingStoriesFrom} ${currentParentFolders.first().displayName}:`}
          {displayStoryList(selectedStories)}
        </Typography>
      );
    }
  } else if (selectedStories.first() && folderMap[selectedStories.first().folderId]) {
    return (
      <Typography variant="body2" data-test="single-story-selected-description">
        {`Moving ${selectedStories.first().title} from ${folderMap[selectedStories.first().folderId].displayName}:`}
      </Typography>
    );
  }
  return null;
};

export const MoveStoryModal: React.FC<IMoveStoryModalProps> = props => {
  const {
    currentFolderId,
    publicationId,
    entitySubdomain,
    folderStructure,
    messages,
    selectedStories,
    handleMoveStoriesSuccess,
    onClose
  } = props;

  const moveStoriesMutation = useMoveStories({ title: messages.somethingWentWrongError });

  const [targetFolderId, setTargetFolderId] = useState(
    currentFolderId ? currentFolderId : folderStructure && folderStructure[0] && folderStructure[0].id
  );

  return (
    <div data-test="move-story-modal">
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="move-story-dialog-title">
        <DialogTitle id="move-story-dialog-title" sx={styles.dialogTitle}>
          Move Story
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          {selectedStories.size > 0 && renderModalDescription(selectedStories, folderStructure, messages)}
          <Typography variant="body2" sx={styles.formLabel}>{messages.location}</Typography>
          <Box sx={styles.locationSelectContainer}>
            <FolderTreeSelect
              activeNodeId={targetFolderId}
              data={folderStructure}
              dataTest="folder-accordion-select"
              disabled={!folderStructure}
              maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
              onNodeSelect={node => setTargetFolderId(node.id)}
              showFilter={true}
              filterPlaceholder={messages.filterFolders}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={onClose}
            data-test="cancel-move-story-button"
          >
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              moveStoriesMutation.mutate(
                { ids: selectedStories.keySeq().toArray(), folderId: targetFolderId },
                {
                  onSuccess: () => {
                    handleMoveStoriesSuccess();
                    if (!!publicationId) {
                      navigateToPublicationFolderList(entitySubdomain, publicationId, targetFolderId);
                    } else {
                      navigateToFolderList(entitySubdomain, targetFolderId);
                    }
                  }
                }
              );
            }}
            data-test="move-story-button"
            disabled={!targetFolderId}
          >
            {messages.move}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
